import { useEffect, useRef, useMemo, useState } from 'react';
import { usePromptStore } from '../../store/usePromptStore';
import { SKILL_LABELS, type PromptEntry } from '../../types';
import * as api from '../../api/endpoints';

export function PromptsTab() {
  // Load existing prompts on mount
  useEffect(() => {
    (async () => {
      const existing = await api.getPrompts();
      if (existing && existing.length > 0) {
        usePromptStore.getState().setPrompts(existing);
      }
    })();
  }, []);

  return (
    <div className="prompt-container">
      <PromptSidebar />
      <PromptFeed />
    </div>
  );
}

function PromptSidebar() {
  const prompts = usePromptStore((s) => s.prompts);
  const skillFilter = usePromptStore((s) => s.skillFilter);
  const typeFilter = usePromptStore((s) => s.typeFilter);
  const setSkillFilter = usePromptStore((s) => s.setSkillFilter);
  const setTypeFilter = usePromptStore((s) => s.setTypeFilter);

  // Stats
  const stats = useMemo(() => {
    const totalTime = prompts.reduce((s, p) => s + (p.elapsed_ms || 0), 0);
    const avgTime = prompts.length > 0 ? totalTime / prompts.length : 0;

    const bySkill: Record<string, { count: number; time: number }> = {};
    for (const p of prompts) {
      if (!bySkill[p.skill]) bySkill[p.skill] = { count: 0, time: 0 };
      bySkill[p.skill].count++;
      bySkill[p.skill].time += p.elapsed_ms || 0;
    }

    return { total: prompts.length, totalTime, avgTime, bySkill };
  }, [prompts]);

  const maxCount = Math.max(...Object.values(stats.bySkill).map((s) => s.count), 1);

  // Available skills for filter
  const skillOptions = useMemo(
    () => [...new Set(prompts.map((p) => p.skill))].sort(),
    [prompts],
  );

  return (
    <div className="prompt-sidebar">
      <h3>Summary</h3>
      <div className="prompt-stats">
        <div className="stat-row">
          <span className="stat-label">Total calls</span>
          <span className="stat-value">{stats.total}</span>
        </div>
        <div className="stat-row">
          <span className="stat-label">Total time</span>
          <span className="stat-value">{(stats.totalTime / 1000).toFixed(1)}s</span>
        </div>
        <div className="stat-row">
          <span className="stat-label">Avg time</span>
          <span className="stat-value">{(stats.avgTime / 1000).toFixed(1)}s</span>
        </div>
      </div>

      <h3>By Skill</h3>
      <div className="prompt-skill-stats">
        {Object.entries(stats.bySkill)
          .sort((a, b) => b[1].count - a[1].count)
          .map(([skill, s]) => (
            <div key={skill} className="prompt-skill-row">
              <span className="prompt-skill-label">
                {SKILL_LABELS[skill] || skill}
              </span>
              <div className="prompt-skill-bar-bg">
                <div
                  className="prompt-skill-bar"
                  style={{ width: `${(s.count / maxCount) * 100}%` }}
                />
              </div>
              <span className="prompt-skill-count">{s.count}</span>
            </div>
          ))}
      </div>

      <h3>Filter</h3>
      <select
        value={skillFilter}
        onChange={(e) => setSkillFilter(e.target.value)}
      >
        <option value="">All skills</option>
        {skillOptions.map((s) => (
          <option key={s} value={s}>
            {SKILL_LABELS[s] || s}
          </option>
        ))}
      </select>
      <select
        value={typeFilter}
        onChange={(e) => setTypeFilter(e.target.value)}
      >
        <option value="">All types</option>
        <option value="text">Text</option>
        <option value="vision">Vision</option>
        <option value="multi_image">Multi-image</option>
        <option value="pdf">PDF</option>
      </select>
    </div>
  );
}

function PromptFeed() {
  const prompts = usePromptStore((s) => s.prompts);
  const skillFilter = usePromptStore((s) => s.skillFilter);
  const typeFilter = usePromptStore((s) => s.typeFilter);
  const feedRef = useRef<HTMLDivElement>(null);

  const filteredPrompts = useMemo(() => {
    return prompts.filter((p) => {
      if (skillFilter && p.skill !== skillFilter) return false;
      if (typeFilter && p.type !== typeFilter) return false;
      return true;
    });
  }, [prompts, skillFilter, typeFilter]);

  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [filteredPrompts.length]);

  if (filteredPrompts.length === 0) {
    return (
      <div className="prompt-feed" ref={feedRef}>
        <div className="feed-placeholder">No LLM calls recorded yet...</div>
      </div>
    );
  }

  return (
    <div className="prompt-feed" ref={feedRef}>
      {filteredPrompts.map((entry, i) => (
        <PromptCard key={i} entry={entry} />
      ))}
    </div>
  );
}

function PromptCard({ entry }: { entry: PromptEntry }) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggle = (section: string) => {
    setExpanded((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const label = SKILL_LABELS[entry.skill] || entry.skill;
  const elapsed = entry.elapsed_ms ? `${(entry.elapsed_ms / 1000).toFixed(1)}s` : '';
  const time = entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString() : '';

  const sections = [
    { key: 'system', label: 'System Prompt', content: entry.system_prompt },
    { key: 'instructions', label: 'Agent Instructions', content: entry.agent_instructions },
    { key: 'prompt', label: 'Prompt', content: entry.prompt },
    { key: 'response', label: 'Response', content: entry.response },
  ].filter((s) => s.content);

  return (
    <div className="prompt-card">
      <div className="prompt-card-header">
        <span className="prompt-skill-badge">{label}</span>
        <span className={`prompt-type-badge ${entry.type}`}>{entry.type}</span>
        {entry.page && (
          <span className="prompt-page">
            p.{entry.page}
            {entry.file && ` (${entry.file})`}
          </span>
        )}
        <span className="prompt-elapsed">{elapsed}</span>
        <span className="prompt-time">{time}</span>
      </div>

      {sections.map((s) => (
        <div key={s.key} className="prompt-section">
          <div
            className="prompt-section-toggle"
            onClick={() => toggle(s.key)}
          >
            <span className="toggle-arrow">{expanded[s.key] ? '\u25BE' : '\u25B8'}</span>
            {s.label}
            <span className="char-count">{s.content!.length.toLocaleString()} chars</span>
          </div>
          {expanded[s.key] && (
            <pre className="prompt-section-content">{s.content}</pre>
          )}
        </div>
      ))}

      {entry.has_image && (
        <div className="prompt-image-indicator">
          {entry.image_count ? `${entry.image_count} images` : 'Has image'}
        </div>
      )}
    </div>
  );
}
